/**
 * MarketingLayout — shared shell for every public-facing marketing page.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, Mail, Sun, Moon, MapPin } from 'lucide-react';
import { BRAND, PRIMARY_NAV, FOOTER_LINKS } from '../content';
import { FloatingWhatsApp } from './PremiumBlocks';
import { CtaLink, scrollIfSameHash } from './blocks';
import { Toaster } from '../../../components/ui/sonner';

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem('actanon_theme');
      if (saved === 'dark') return true;
      if (saved === 'light') return false;
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('actanon_theme', dark ? 'dark' : 'light'); } catch {}
  }, [dark]);
  return [dark, setDark];
}

function ThemeToggle() {
  const [dark, setDark] = useDarkMode();
  const label = dark ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <button onClick={() => setDark((d) => !d)} className="h-10 w-10 grid place-items-center rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition-colors" aria-label={label} title={label} data-testid="theme-toggle">
      {dark ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
    </button>
  );
}

function Logo({ testid }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid={testid} aria-label="ActanonEdu home">
      <img src="/actanon-logo-192.png" alt="" width="36" height="36" className="h-9 w-9" loading="eager" />
      <span className="font-heading font-extrabold tracking-tight text-white text-lg">{BRAND.name}</span>
    </Link>
  );
}

const navId = (label) => label.toLowerCase().replace(/\s/g, '-');

function useIsActive() {
  const { pathname, hash } = useLocation();
  return (href) => {
    const [path, h] = href.split('#');
    if (h) return pathname === (path || '/') && hash === `#${h}`;
    if (path === '/') return pathname === '/' && !hash;
    return pathname === path || pathname.startsWith(`${path}/`);
  };
}

function MarketingNav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isActive = useIsActive();

  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const linkCls = (href) => `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive(href) ? 'text-white bg-white/10' : 'text-slate-300 hover:text-white'}`;

  return (
    <header data-testid="marketing-nav" className="sticky top-0 z-50 bg-mk-midnight/95 backdrop-blur-md border-b border-mk-edge">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:bg-white focus:text-mk-ink focus:px-3 focus:py-2 focus:rounded-md">Skip to content</a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo testid="marketing-nav-logo" />
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {PRIMARY_NAV.map((item) => (
            <Link key={item.href} to={item.href} className={linkCls(item.href)} aria-current={isActive(item.href) ? 'page' : undefined} onClick={() => scrollIfSameHash(item.href)} data-testid={`marketing-nav-${navId(item.label)}`}>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-1 lg:gap-2">
          <ThemeToggle />
          <div className="hidden lg:flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 text-sm font-semibold text-white hover:text-mk-cyan transition-colors" data-testid="marketing-nav-login">Log In</Link>
          <CtaLink to="/signup" className="!h-10 !px-5" testid="marketing-nav-signup">Get Started</CtaLink>
          </div>
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden h-11 w-11 grid place-items-center rounded-full text-white hover:bg-white/10" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="mk-mobile-menu" data-testid="marketing-nav-toggle">
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && (
        <nav id="mk-mobile-menu" className="lg:hidden border-t border-mk-edge bg-mk-midnight" aria-label="Mobile" data-testid="marketing-nav-mobile">
          <div className="px-4 py-4 space-y-1">
            {PRIMARY_NAV.map((item) => (
              <Link key={item.href} to={item.href} onClick={() => scrollIfSameHash(item.href)} className={`block px-4 py-3 rounded-xl text-base font-medium ${isActive(item.href) ? 'text-white bg-white/10' : 'text-slate-300 hover:bg-white/5'}`} data-testid={`marketing-nav-mobile-${navId(item.label)}`}>
                {item.label}
              </Link>
            ))}
            <div className="pt-4 mt-3 border-t border-mk-edge grid grid-cols-2 gap-3">
              <CtaLink to="/login" variant="ghostDark" testid="marketing-nav-mobile-login">Log In</CtaLink>
              <CtaLink to="/signup" testid="marketing-nav-mobile-signup">Get Started</CtaLink>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer className="bg-mk-midnight text-slate-300 border-t border-mk-edge" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-10">
        <div className="col-span-2 sm:col-span-3 lg:col-span-2">
          <Logo testid="footer-logo" />
          <p className="mt-4 text-sm leading-relaxed text-slate-400 max-w-sm">AI teaching tools and school management, powered by BOSCO AI. Made for education.</p>
          <div className="mt-6 space-y-2.5 text-sm">
            <a href={BRAND.whatsappLink('Hi Founder! I came from the ActanonEdu website.')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-200 hover:text-mk-cyan" data-testid="footer-whatsapp">
              <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp +91 87968 69006
            </a>
            <a href={`mailto:${BRAND.supportEmail}`} className="flex items-center gap-2 text-slate-200 hover:text-mk-cyan" data-testid="footer-email">
              <Mail className="h-4 w-4" aria-hidden="true" /> {BRAND.supportEmail}
            </a>
            <p className="flex items-start gap-2 text-slate-400" data-testid="footer-address">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{BRAND.legalName}<br />{BRAND.address}</span>
            </p>
          </div>
        </div>
        {Object.entries(FOOTER_LINKS).map(([col, links]) => (
          <nav key={col} aria-label={col}>
            <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-white mb-4">{col}</h2>
            <ul className="space-y-2.5 text-sm">
              {links.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="text-slate-400 hover:text-white transition-colors" data-testid={`footer-link-${l.href.replace(/\//g, '')}`}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="border-t border-mk-edge">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {BRAND.copyrightYear} {BRAND.name}. All rights reserved.</span>
          <span>Made in India for educators.</span>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    return undefined;
  }, [pathname, hash]);
  return (
    <div className="mk-root min-h-screen flex flex-col bg-white dark:bg-mk-midnight text-mk-ink dark:text-slate-100">
      <MarketingNav />
      <main id="main" className="flex-1">{children}</main>
      <MarketingFooter />
      <FloatingWhatsApp />
      <Toaster position="top-center" richColors />
    </div>
  );
}
